import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Users, Mail, Phone, MapPin, Briefcase, Edit, Trash2, FileText } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Input } from '../Components/ui/input';
import { Badge } from '../Components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../Components/ui/dialog';
import { Label } from '../Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';
import { Textarea } from '../Components/ui/textarea';
import { Avatar, AvatarFallback } from '../Components/ui/avatar';

export default function Applicants() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingApplicant, setEditingApplicant] = useState(null);
  const queryClient = useQueryClient();

  const { data: applicants = [] } = useQuery({
    queryKey: ['applicants'],
    queryFn: () => backendApi.entities.Applicant.list('-created_date', 100),
    initialData: []
  });

  const createApplicantMutation = useMutation({
    mutationFn: (data) => backendApi.entities.Applicant.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['applicants']);
      setIsDialogOpen(false);
      setEditingApplicant(null);
    }
  });

  const updateApplicantMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.Applicant.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['applicants']);
      setIsDialogOpen(false);
      setEditingApplicant(null);
    }
  });

  const deleteApplicantMutation = useMutation({
    mutationFn: (id) => backendApi.entities.Applicant.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['applicants']);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    if (editingApplicant) {
      updateApplicantMutation.mutate({ id: editingApplicant.id, data });
    } else {
      createApplicantMutation.mutate(data);
    }
  };

  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = applicant.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         applicant.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         applicant.current_position?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || applicant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusColors = {
    'New': 'bg-blue-100 text-blue-800 border-blue-300',
    'Screening': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Interview': 'bg-purple-100 text-purple-800 border-purple-300',
    'Offer': 'bg-green-100 text-green-800 border-green-300',
    'Rejected': 'bg-red-100 text-red-800 border-red-300',
    'Hired': 'bg-emerald-100 text-emerald-800 border-emerald-300'
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Applicants</h1>
              <p className="text-sm text-gray-500">Manage all candidate applications</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingApplicant(null)}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Applicant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingApplicant ? 'Edit Applicant' : 'Add New Applicant'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input name="full_name" defaultValue={editingApplicant?.full_name} required />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" name="email" defaultValue={editingApplicant?.email} required />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input name="phone" defaultValue={editingApplicant?.phone} />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input name="location" defaultValue={editingApplicant?.location} />
                  </div>
                  <div>
                    <Label>Current Position</Label>
                    <Input name="current_position" defaultValue={editingApplicant?.current_position} />
                  </div>
                  <div>
                    <Label>Current Company</Label>
                    <Input name="current_company" defaultValue={editingApplicant?.current_company} />
                  </div>
                  <div>
                    <Label>Experience (Years)</Label>
                    <Input type="number" name="experience_years" defaultValue={editingApplicant?.experience_years} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingApplicant?.status || 'New'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New">New</SelectItem>
                        <SelectItem value="Screening">Screening</SelectItem>
                        <SelectItem value="Interview">Interview</SelectItem>
                        <SelectItem value="Offer">Offer</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                        <SelectItem value="Hired">Hired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Select name="source" defaultValue={editingApplicant?.source || 'Direct Application'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                        <SelectItem value="Indeed">Indeed</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Direct Application">Direct Application</SelectItem>
                        <SelectItem value="Agency">Agency</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Resume URL</Label>
                    <Input name="resume_url" defaultValue={editingApplicant?.resume_url} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea name="notes" defaultValue={editingApplicant?.notes} rows={3} />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingApplicant ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
          <Input
            placeholder="Search by name, email, or position..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-white shadow-sm border-gray-200"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-12 bg-white shadow-sm">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Screening">Screening</SelectItem>
            <SelectItem value="Interview">Interview</SelectItem>
            <SelectItem value="Offer">Offer</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="Hired">Hired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Applicants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredApplicants.map((applicant) => (
          <Card key={applicant.id} className="hover:shadow-lg transition-all duration-300 border border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-start space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-purple-600 text-white font-semibold">
                    {applicant.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">{applicant.full_name}</CardTitle>
                  <Badge className={`${statusColors[applicant.status]} border mt-1`}>
                    {applicant.status}
                  </Badge>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingApplicant(applicant);
                      setIsDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this applicant?')) {
                        deleteApplicantMutation.mutate(applicant.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {applicant.current_position && (
                <div className="flex items-center text-sm text-gray-600">
                  <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{applicant.current_position}</span>
                </div>
              )}
              {applicant.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{applicant.email}</span>
                </div>
              )}
              {applicant.phone && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{applicant.phone}</span>
                </div>
              )}
              {applicant.location && (
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{applicant.location}</span>
                </div>
              )}
              {applicant.experience_years && (
                <div className="text-sm font-medium text-blue-600">
                  {applicant.experience_years} years experience
                </div>
              )}
              {applicant.resume_url && (
                <a 
                  href={applicant.resume_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:underline"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  View Resume
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredApplicants.length === 0 && (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applicants found</h3>
            <p className="text-gray-500">Add your first applicant to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}